import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const REVIEW_STATUS_OPTIONS = ["REGISTERED", "PARSING", "PARSED", "REVIEW_REQUIRED", "PARTIALLY_APPROVED", "APPROVED", "SUPERSEDED", "ARCHIVED", "REJECTED"];

export default function TrainingSourceDetailPage() {
  const { sourceId } = useParams();
  const { t } = useLocalization();
  const { canViewTraining, canReviewTrainingSource } = usePermissions();
  const [source, setSource] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sections, setSections] = useState([]);

  const load = useCallback(() => {
    trainingApi.getSource(sourceId).then((r) => { setSource(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
    trainingApi.listSections(sourceId).then((r) => setSections(r.data)).catch(() => setSections([]));
  }, [sourceId]);

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining, load]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="training-source-detail-access-denied">{t("common.accessDenied")}</p>;
  if (notFound) return <NotFoundState testId="training-source-not-found" backTo="/management/training/sources" />;
  if (!source) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const updateSourceStatus = async (status) => {
    try { await trainingApi.reviewSource(sourceId, { review_status: status }); toast.success(t("mgmtTrainingSourceDetail.sourceStatusUpdated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const classify = async (sectionId, field, value) => {
    try { await trainingApi.classifySection(sectionId, { [field]: value }); toast.success(t("mgmtTrainingSourceDetail.sectionUpdated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const FIELD_LABELS = {
    legal_review_status: t("mgmtContentReview.legal"),
    food_safety_review_status: t("mgmtContentReview.foodSafety"),
    management_approval_status: t("mgmtContentReview.management"),
  };

  return (
    <div className="space-y-5" data-testid="training-source-detail-page">
      <PageHelpBanner pageKey="training-source-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtTrainingSourceDetail.help")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{source.source_id} - {source.original_filename}</h2>
          <p className="text-sm text-gray-500">{source.source_type} · {source.language} · {sections.length} {t("mgmtTrainingSourceDetail.sections")}</p>
        </div>
        <StatusBadge status={source.review_status} />
      </div>

      {canReviewTrainingSource && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-2" data-testid="source-status-actions">
          {REVIEW_STATUS_OPTIONS.map((s) => (
            <button key={s} data-testid={`source-status-btn-${s}`} onClick={() => updateSourceStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${source.review_status === s ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 hover:bg-gray-50"}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {sections.map((sec) => (
          <div key={sec.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`section-row-${sec.id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtContentReview.section")} {sec.section_index} · {sec.page_or_section_ref}</p>
              {sec.review_warning && <span className="text-xs text-amber-700" data-testid={`section-warning-${sec.id}`}>{sec.review_warning}</span>}
            </div>
            <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{sec.original_text}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {["legal_review_status", "food_safety_review_status", "management_approval_status"].map((field) => (
                <div key={field}>
                  <label className="text-xs text-gray-500 block mb-1">{FIELD_LABELS[field]}</label>
                  <select
                    data-testid={`section-${field}-select-${sec.id}`}
                    disabled={!canReviewTrainingSource}
                    value={sec[field] || "OPEN"}
                    onChange={(e) => classify(sec.id, field, e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-full"
                  >
                    {["OPEN", "UNDER_LEGAL_REVIEW", "APPROVED", "NOT_APPLICABLE", "REJECTED"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
        {sections.length === 0 && <p className="text-sm text-gray-500 text-center py-6" data-testid="no-sections-row">{t("mgmtTrainingSourceDetail.noSectionsYet")}</p>}
      </div>
    </div>
  );
}
